import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Users as UsersIcon, Plus, Search, Loader2, AlertTriangle,
  KeyRound, MoreVertical, Power, PowerOff, UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useTenantUsers, useCreateTenantUser, useResetTenantUserPassword,
  useActivateTenantUser, useDeactivateTenantUser,
} from '../hooks/api/users';
import { useMe, useActiveClient, usePermission } from '../hooks/useMe';

function genTemporaryPassword() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < 14; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out + '!9';
}

export function UserManagement() {
  const me = useMe();
  const activeClient = useActiveClient();
  const canInvite  = usePermission('user:invite');
  const canUpdate  = usePermission('user:update');

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [resetForUser, setResetForUser] = useState(null); // user object or null

  const { data, isLoading, isError, error, refetch } = useTenantUsers({
    q: search.trim() || undefined,
    page_size: 200,
  });

  const items = data?.data || [];

  const dim = (active) => active ? '' : 'opacity-60';

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <UsersIcon className="w-6 h-6" /> Users
            {activeClient && (
              <Badge variant="outline" className="text-[10px] font-mono">
                {activeClient.code}
              </Badge>
            )}
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage members of {activeClient?.name || 'this client'}. Reset
            passwords, deactivate access, or invite new users.
          </p>
        </div>
        {canInvite && (
          <Button onClick={() => setCreateOpen(true)}>
            <UserPlus className="w-4 h-4 mr-2" /> Add User
          </Button>
        )}
      </header>

      <Card className="border-0 bg-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Members {data?.total != null && <span className="text-muted-foreground">({data.total})</span>}</span>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && (
            <div className="py-10 flex items-center gap-2 justify-center text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
            </div>
          )}
          {isError && (
            <div className="py-6 px-6 text-sm text-destructive flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {error?.message}
            </div>
          )}
          {!isLoading && !isError && items.length === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No users yet. {canInvite && 'Click "Add User" to invite the first member.'}
            </div>
          )}
          {!isLoading && items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left py-2 px-4 font-medium">User</th>
                    <th className="text-left py-2 px-4 font-medium">Code</th>
                    <th className="text-left py-2 px-4 font-medium">Role</th>
                    <th className="text-left py-2 px-4 font-medium">Status</th>
                    <th className="text-left py-2 px-4 font-medium">Last login</th>
                    <th className="text-right py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((u) => (
                    <tr key={u.id} className={`border-b border-border/40 ${dim(u.is_active)}`}>
                      <td className="py-3 px-4">
                        <div className="font-medium">{u.full_name}</div>
                        <div className="text-[11px] font-mono text-muted-foreground">{u.email}</div>
                      </td>
                      <td className="py-3 px-4 font-mono text-xs">{u.code || '—'}</td>
                      <td className="py-3 px-4">
                        {u.is_client_admin
                          ? <Badge>Client Admin</Badge>
                          : <Badge variant="secondary">User</Badge>}
                      </td>
                      <td className="py-3 px-4">
                        {u.is_active
                          ? <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] border bg-emerald-500/15 text-emerald-300 border-emerald-500/30">active</span>
                          : <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] border bg-slate-500/15 text-slate-300 border-slate-500/30">inactive</span>}
                      </td>
                      <td className="py-3 px-4 text-xs text-muted-foreground">
                        {u.last_login_at ? new Date(u.last_login_at).toISOString().slice(0, 16).replace('T', ' ') : '—'}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {canUpdate && u.id !== me?.id && (
                          <RowMenu
                            user={u}
                            onResetPwd={() => setResetForUser(u)}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {createOpen && (
        <CreateUserDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(u) => {
            toast.success(`Invited ${u.full_name} (${u.code || u.email})`);
            refetch();
          }}
        />
      )}

      {resetForUser && (
        <ResetPasswordDialog
          user={resetForUser}
          open={Boolean(resetForUser)}
          onOpenChange={(v) => !v && setResetForUser(null)}
          onDone={() => setResetForUser(null)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Per-row dropdown                                                           */
/* -------------------------------------------------------------------------- */
function RowMenu({ user, onResetPwd }) {
  const activate = useActivateTenantUser();
  const deactivate = useDeactivateTenantUser();
  const handle = async (action) => {
    try {
      if (action === 'deactivate') await deactivate.mutateAsync(user.id);
      else if (action === 'activate') await activate.mutateAsync(user.id);
    } catch (e) {
      toast.error(e?.message || 'Action failed');
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onResetPwd}>
          <KeyRound className="w-4 h-4 mr-2" /> Reset password
        </DropdownMenuItem>
        {user.is_active ? (
          <DropdownMenuItem onClick={() => handle('deactivate')} className="text-destructive">
            <PowerOff className="w-4 h-4 mr-2" /> Deactivate
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => handle('activate')}>
            <Power className="w-4 h-4 mr-2" /> Activate
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* -------------------------------------------------------------------------- */
/* Create-user dialog                                                          */
/* -------------------------------------------------------------------------- */
function CreateUserDialog({ open, onOpenChange, onCreated }) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [pwd, setPwd] = useState(genTemporaryPassword());
  const [error, setError] = useState('');

  const create = useCreateTenantUser();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!fullName.trim() || !email.trim() || !pwd) {
      setError('Name, email, and password are required.');
      return;
    }
    try {
      const u = await create.mutateAsync({
        full_name: fullName.trim(),
        email: email.trim(),
        temporary_password: pwd,
        role,
      });
      onCreated?.(u);
      onOpenChange(false);
    } catch (err) {
      const errs = err?.details?.errors;
      if (Array.isArray(errs) && errs.length > 0) {
        const path = (errs[0].loc || []).slice(1).join('.') || 'request';
        setError(`${path}: ${errs[0].msg}`);
      } else {
        setError(err?.message || 'Failed to create user');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" /> Add User
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Full name <span className="text-red-500">*</span></Label>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="e.g. Hydraa User 02" />
          </div>
          <div className="space-y-2">
            <Label>Email <span className="text-red-500">*</span></Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user02@hydraa.gov" />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Client Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Temporary password <span className="text-red-500">*</span></Label>
            <div className="flex gap-2">
              <Input value={pwd} onChange={(e) => setPwd(e.target.value)} />
              <Button type="button" variant="outline" onClick={() => setPwd(genTemporaryPassword())}>
                Regenerate
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Share this with the user — they should change it on first login.
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/* Reset-password dialog                                                       */
/* -------------------------------------------------------------------------- */
function ResetPasswordDialog({ user, open, onOpenChange, onDone }) {
  const [pwd, setPwd] = useState(genTemporaryPassword());
  const [error, setError] = useState('');
  const reset = useResetTenantUserPassword();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!pwd || pwd.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    try {
      await reset.mutateAsync({ userId: user.id, password: pwd });
      toast.success(`Password reset for ${user.full_name}`);
      onDone?.();
    } catch (err) {
      setError(err?.message || 'Reset failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" /> Reset Password
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Set a new temporary password for <strong>{user.full_name}</strong> ({user.email}).
          </div>
          <div className="space-y-2">
            <Label>New password</Label>
            <div className="flex gap-2">
              <Input value={pwd} onChange={(e) => setPwd(e.target.value)} />
              <Button type="button" variant="outline" onClick={() => setPwd(genTemporaryPassword())}>
                Regenerate
              </Button>
            </div>
          </div>
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={reset.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={reset.isPending}>
              {reset.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Reset
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
